import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  setPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { getUserInfoCountByEmail } from "./firebase";
import { defaultProfileImages } from "./constants";
import userModel from "../lib/firebase/userModel";
import { prodFirebaseConfig, sandboxFirebaseConfig } from "./firebaseConfigs";
import { initializeApp, getApp } from "firebase/app";
import { isUserBanned, isUserSuspended } from "./report";
import { getExpressApiBaseUrl } from "../lib/api/config";

console.log(
  "[authentication.js] Starting Firebase initialization at:",
  new Date().toISOString(),
  "NODE_ENV:",
  process.env.NODE_ENV
);

let config;
switch (process.env.REACT_APP_DEPLOYMENT) {
  case "prod":
    config = prodFirebaseConfig;
    break;
  default:
    config = sandboxFirebaseConfig;
}

console.log(
  "[authentication.js] Using config for deployment:",
  process.env.REACT_APP_DEPLOYMENT
);

if (!config.apiKey) {
  const forProd = process.env.REACT_APP_DEPLOYMENT === "prod";
  const varName = forProd ? "FIREBASE_WEB_API_KEY_PROD" : "FIREBASE_WEB_API_KEY";
  throw new Error(
    `[Clumse] Missing ${varName} in backend/.env.local (Firebase Console → Web app apiKey; not the Admin SDK). ` +
      "Restart the dev server after editing."
  );
}

// Check if Firebase app already exists to prevent multiple instances
let app;
try {
  app = initializeApp(config);
  console.log("[authentication.js] Firebase app initialized");
} catch (error) {
  if (error.code === "app/duplicate-app") {
    console.log(
      "[authentication.js] Firebase app already exists, using existing instance"
    );
    app = getApp();
  } else {
    console.error(
      "[authentication.js] Error initializing Firebase app:",
      error
    );
    throw error;
  }
}

// Initialize Auth with fast persistence (avoid IndexedDB delays on refresh)
let firebaseAuth;
try {
  // Prefer localStorage to avoid Safari/IndexedDB stalls, then fall back to IndexedDB
  firebaseAuth = initializeAuth(app, {
    persistence: [browserLocalPersistence, indexedDBLocalPersistence],
  });
  console.log(
    "[authentication.js] Firebase auth initialized with custom persistence"
  );
} catch (e) {
  // If already initialized elsewhere, get existing instance and ensure persistence is set
  firebaseAuth = getAuth(app);
  console.log(
    "[authentication.js] Firebase auth acquired via getAuth (already initialized)"
  );

  // CRITICAL: Explicitly set persistence even if auth was already initialized
  // This ensures persistence works on mobile/PWA where storage can be cleared
  // Note: setPersistence is async, but we don't need to await it here
  // Firebase will use the persistence setting for future auth operations
  setPersistence(firebaseAuth, browserLocalPersistence)
    .then(() => {
      console.log(
        "[authentication.js] Persistence set to browserLocalPersistence"
      );
    })
    .catch((persistError) => {
      console.warn(
        "[authentication.js] Could not set browserLocalPersistence, trying IndexedDB:",
        persistError
      );
      // Try IndexedDB as fallback
      setPersistence(firebaseAuth, indexedDBLocalPersistence)
        .then(() => {
          console.log(
            "[authentication.js] Persistence set to indexedDBLocalPersistence"
          );
        })
        .catch((indexedError) => {
          console.error(
            "[authentication.js] Could not set any persistence - auth may not persist across sessions:",
            indexedError
          );
        });
    });
}

export { firebaseAuth };
console.log("[authentication.js] Firebase auth initialized");

export const GOOGLE_OAUTH_CLIENT_ID =
  "234934930439-mfjt2tsbcba7tkhgom4hd33vr0vna35i.apps.googleusercontent.com";

export const authRoutes = ["/login", "/signup", "/forgot-password"];

/**
 * Same character set as backend `WebhookService.generatePassword` (Stripe webhook).
 * Used when the UI does not collect a password (e.g. Free Gateway signup).
 */
export function generateStripeStyleTemporaryPassword(length = 16) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=";
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const out = new Uint8Array(length);
    crypto.getRandomValues(out);
    let password = "";
    for (let i = 0; i < length; i++) {
      password += chars.charAt(out[i] % chars.length);
    }
    return password;
  }
  return `Lo${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export async function registerFirebaseAuth({ email, password }) {
  try {
    const profileCount = await getUserInfoCountByEmail(email);
    if (profileCount >= 1) return false;
    const result = await createUserWithEmailAndPassword(
      firebaseAuth,
      email.toLowerCase(),
      password
    );
    return result;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export async function loginUser(email, password) {
  console.log("[loginUser] Starting login process for:", email);
  const startTime = performance.now();

  try {
    const credentials = await signInWithEmailAndPassword(
      firebaseAuth,
      email,
      password
    );

    const loginTime = performance.now() - startTime;
    console.log(
      `[loginUser] Firebase auth completed in ${loginTime.toFixed(2)}ms`
    );

    // Unclear if this would ever occur, signInWithEmailAndPassword should throw an error on incorrect auth
    if (!credentials) {
      throw new Error("Something went wrong logging in");
    }

    // For production, skip ban/suspension checks initially to speed up login
    const isProduction = process.env.NODE_ENV === "production";

    if (!isProduction) {
      // Check ban status (non-blocking for performance)
      const banCheckPromise = isUserBanned(credentials.user.uid);
      const suspensionCheckPromise = isUserSuspended(credentials.user.uid);

      // Wait for both checks with timeout
      const [isBanned, isSuspended] = await Promise.race([
        Promise.all([banCheckPromise, suspensionCheckPromise]),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout")), 3000)
        ),
      ]).catch(() => {
        console.warn("[loginUser] Ban/suspension check timeout, proceeding");
        return [false, { suspended: false }];
      });

      if (isBanned) {
        firebaseAuth.signOut();
        throw { code: "banned" };
      }

      if (isSuspended.suspended) {
        firebaseAuth.signOut();
        throw { code: "suspended", expiresAt: isSuspended.expiresAt };
      }
    } else {
      // In production, do these checks asynchronously after login
      setTimeout(async () => {
        try {
          const [isBanned, isSuspended] = await Promise.all([
            isUserBanned(credentials.user.uid),
            isUserSuspended(credentials.user.uid),
          ]);

          if (isBanned) {
            console.warn("[loginUser] User is banned, signing out");
            firebaseAuth.signOut();
          } else if (isSuspended.suspended) {
            console.warn("[loginUser] User is suspended, signing out");
            firebaseAuth.signOut();
          }
        } catch (error) {
          console.error(
            "[loginUser] Error in async ban/suspension check:",
            error
          );
        }
      }, 100);
    }

    // Get user info (non-blocking)
    const userInfoPromise = userModel.getOneById(credentials.user.uid);
    const userInfo = await Promise.race([
      userInfoPromise,
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Timeout")),
          isProduction ? 1000 : 2000
        )
      ),
    ]).catch(() => {
      console.warn("[loginUser] User info fetch timeout, using basic info");
      return {
        id: credentials.user.uid,
        email: credentials.user.email,
        displayName: credentials.user.displayName || credentials.user.email,
      };
    });

    // Set firstLoginAt timestamp if this is the user's first login
    if (userInfo && !userInfo.firstLoginAt) {
      try {
        await userModel.update({
          id: credentials.user.uid,
          data: {
            firstLoginAt: new Date().toISOString(),
          },
        });
        console.log(
          "[loginUser] Set firstLoginAt timestamp for user:",
          credentials.user.uid
        );
      } catch (error) {
        console.error("[loginUser] Error setting firstLoginAt:", error);
        // Don't fail login if this update fails
      }
    }

    const totalTime = performance.now() - startTime;
    console.log(
      `[loginUser] Total login process completed in ${totalTime.toFixed(2)}ms`
    );

    return userInfo;
  } catch (error) {
    const totalTime = performance.now() - startTime;
    console.error(
      `[loginUser] Login failed after ${totalTime.toFixed(2)}ms:`,
      error
    );
    throw error;
  }
}

export function logoutUser(to = "/") {
  firebaseAuth
    .signOut()
    .then(() => {
      // SECURITY: Clear all localStorage to prevent data leakage between user accounts
      try {
        localStorage.clear();
        console.log("✅ [LOGOUT] Cleared localStorage for security");
      } catch (error) {
        console.error("❌ [LOGOUT] Failed to clear localStorage:", error);
      }
      
      // Also clear sessionStorage for completeness
      try {
        sessionStorage.clear();
        console.log("✅ [LOGOUT] Cleared sessionStorage for security");
      } catch (error) {
        console.error("❌ [LOGOUT] Failed to clear sessionStorage:", error);
      }
      
      window.location.href = to;
    })
    .catch((error) => {
      console.error("Sign out error:", error);
    });
}

export function validateEmail(email) {
  const res =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return res.test(String(email).toLowerCase());
}

export function getRandomProfilePic() {
  return defaultProfileImages[
    Math.floor(Math.random() * defaultProfileImages.length)
  ];
}

export function getDefaultProfilePicByUserId(userId) {
  return defaultProfileImages[
    userId.charCodeAt(0) % defaultProfileImages.length
  ];
}

export async function doesUserAuthExistByEmail(email) {
  const response = await fetch(`${getExpressApiBaseUrl()}/doesUserExist`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user");
  }

  return response.json();
}
